/**
 * Змій Горинич: what he does, as rules over numbers.
 *
 * Pavlo settled the behaviour in two messages and it is all here:
 *
 *   "він літає - з рота стріляє вогняними кулями, охороняє лігво - тобто
 *    далеко від нього не відходить. після нападу відлітає а потім знову
 *    нападає - тобто постійно гравця не переслідує."
 *   "після смерті не відроджується, запали один блок в який попадає вогнем,
 *    наносить шкоди на 3 серця коли без броні, атакує першим."
 *
 * Which is a different animal from everything else in this game. A звір
 * grazes and bolts; an орк walks at you and keeps walking until one of you
 * is down. This one holds a station over its own лігво, comes in when
 * somebody is close enough to be worth a shot, breathes, and leaves - and
 * then comes back. A player who stands still is shot at; a player who runs
 * is not followed to the end of the map.
 *
 * No world, no rendering, no items. The caller says where the ground is and
 * gets told where he goes and when he breathes.
 */

/** Hearts, and a heart is two points: `BOLT_DAMAGE` is the same number. */
import { BEAST_SKINS } from "./beastSkin.js";
import { BREEDS } from "./beast.js";

export const BREATH_DAMAGE = 6;

/** How far from the middle of his лігво he will go, in blocks. */
export const LEASH = 34;

/**
 * Inside this of the лігво, somebody is worth coming in for.
 *
 * Sixty-four, and it used to be forty - which made the leash below it dead
 * code. He stands off by four fifths of his breath range to shoot, so at
 * forty he would never fly further than twenty-six blocks from the hall and
 * the leash could never bind: I tried to plant its removal and nothing
 * moved. A number that keeps him home by accident, out of the arithmetic of
 * three other numbers, is a number that stops keeping him home the day one
 * of them changes.
 *
 * Sixty-four is also the better answer to "атакує першим". A flying thing
 * that only notices you at forty blocks notices you when you are already
 * most of the way in.
 */
export const NOTICE = 64;

/** He breathes from here, and no nearer: a dragon is not a biting animal. */
export const BREATH_RANGE = 18;

/** Seconds between one breath and the next, while he is making a pass. */
export const BREATH_GAP = 1.4;

/** How long one pass lasts before he peels off, in seconds. */
export const PASS_SECONDS = 6;

/** And how long he keeps away afterwards. */
export const AWAY_SECONDS = 5;

/** How high over his лігво he holds station. */
export const STATION_HEIGHT = 16;

/** Blocks a second, level. */
export const CRUISE = 7;

export type DragonMood = "guard" | "pass" | "away";

export interface Dragon {
  readonly id: number;
  /** The middle of the hall he was cut into, which he never really leaves. */
  readonly home: { readonly x: number; readonly y: number; readonly z: number };
  x: number;
  y: number;
  z: number;
  yaw: number;
  hp: number;
  mood: DragonMood;
  /** Seconds left of whatever he is doing. */
  timer: number;
  /** Seconds until he can breathe again. */
  reload: number;
  /** Counted up, so a client can tell one breath from the same breath. */
  breaths: number;
  /** Set once, when he goes down. A лігво is emptied once and stays empty. */
  slain: boolean;
}

let nextId = 1;

export function makeDragon(
  home: { x: number; y: number; z: number }, maxHp: number,
): Dragon {
  return {
    id: nextId++,
    home: { ...home },
    // Over the mouth of his own hall, in the air: he is flying before
    // anybody arrives, which is how "атакує першим" reads from a distance.
    x: home.x, y: home.y + STATION_HEIGHT, z: home.z,
    yaw: 0, hp: maxHp,
    mood: "guard", timer: 0, reload: 0, breaths: 0, slain: false,
  };
}

export interface Target {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * Where the Змій's head is, in the model's own terms.
 *
 * "Make dragon fire go from his head, not its centre." The breath used to
 * leave from his middle, one block down - six and a half blocks under the
 * head the model actually draws. The model places the head from the skin's
 * proportions; this is that same arithmetic, kept here so the model and the
 * breath cannot disagree: the model reads these numbers back rather than
 * working them out a second time, and the model stage checks the two agree
 * for several turns of his head.
 *
 * `up` is above his feet (the model's origin), `forward` is along the way he
 * faces. Blocks, not pixels.
 */
export function dragonHeadLocal(): { readonly up: number; readonly forward: number } {
  const p = BEAST_SKINS.zmii.parts;
  const px = BREEDS.zmii.height / p.standing;
  const neck = p.neck!;
  return {
    up: p.leg.h * px + p.body.h * px + neck.d * px * 0.62,
    forward: (p.body.d * px) / 2 + neck.d * px * 0.55,
  };
}

/**
 * The head in the world, given where he is and which way he faces.
 *
 * The model turns by `rotation.y = yaw` and holds the head down its own -z,
 * so a local forward of one is (-sin yaw, 0, -cos yaw) in the world.
 */
export function dragonHeadWorld(
  d: { readonly x: number; readonly y: number; readonly z: number; readonly yaw: number },
): { x: number; y: number; z: number } {
  const { up, forward } = dragonHeadLocal();
  return {
    x: d.x - Math.sin(d.yaw) * forward,
    y: d.y + up,
    z: d.z - Math.cos(d.yaw) * forward,
  };
}

/** The mouth: a head and a half further forward than the head's middle. */
export function dragonMouth(
  d: { readonly x: number; readonly y: number; readonly z: number; readonly yaw: number },
): { x: number; y: number; z: number } {
  const p = BEAST_SKINS.zmii.parts;
  const px = BREEDS.zmii.height / p.standing;
  const ahead = (p.head.d / 2 + p.muzzle.d) * px;
  const head = dragonHeadWorld(d);
  return {
    x: head.x - Math.sin(d.yaw) * ahead,
    y: head.y - (p.head.h * px) / 4,
    z: head.z - Math.cos(d.yaw) * ahead,
  };
}

/** A breath, for the caller to turn into whatever a fireball is. */
export interface Breath {
  readonly from: { x: number; y: number; z: number };
  readonly at: { x: number; y: number; z: number };
}

function nearest(d: Dragon, people: readonly Target[]): Target | null {
  let best: Target | null = null;
  let far = Infinity;
  for (const p of people) {
    const away = Math.hypot(p.x - d.home.x, p.z - d.home.z);
    if (away > NOTICE) continue;
    const gap = Math.hypot(p.x - d.x, p.y - d.y, p.z - d.z);
    if (gap < far) { far = gap; best = p; }
  }
  return best;
}

/**
 * One step for every dragon, and whatever they breathed.
 *
 * `people` is everybody who can be shot at. Nobody is followed: a target is
 * only a target while it is inside `NOTICE` **of the лігво**, not of the
 * dragon - which is the whole of "далеко від нього не відходить", and the
 * reason it is measured from the hall rather than from him. Walk away and he
 * loses interest at a fixed line on the ground, wherever he happens to be.
 */
export function stepDragons(
  dragons: readonly Dragon[], people: readonly Target[], dt: number,
): Breath[] {
  const breaths: Breath[] = [];
  for (const d of dragons) {
    if (d.slain) continue;
    d.timer -= dt;
    d.reload -= dt;
    const mark = nearest(d, people);

    if (d.mood === "away" && d.timer <= 0) d.mood = "guard";
    if (d.mood === "pass" && (d.timer <= 0 || mark === null)) {
      d.mood = "away";
      d.timer = AWAY_SECONDS;
    }
    if (d.mood === "guard" && mark !== null) {
      d.mood = "pass";
      d.timer = PASS_SECONDS;
    }

    /*
     * Where he is going, which is the only thing the three moods differ in.
     *
     * On a pass, at the target but never nearer than the range he breathes
     * from - a dragon that closes to nothing is a dragon you can hit with a
     * sabre while it does so. Away and guarding, back over his own hall,
     * high up. He is never given a heading that leaves the leash.
     */
    let toX = d.home.x, toY = d.home.y + STATION_HEIGHT, toZ = d.home.z;
    if (d.mood === "pass" && mark !== null) {
      const run = Math.hypot(mark.x - d.x, mark.z - d.z) || 1;
      const back = Math.min(run, BREATH_RANGE * 0.8);
      toX = mark.x - ((mark.x - d.x) / run) * back;
      toZ = mark.z - ((mark.z - d.z) / run) * back;
      toY = mark.y + 9;
    }
    const dx = toX - d.x, dy = toY - d.y, dz = toZ - d.z;
    const span = Math.hypot(dx, dy, dz);
    if (span > 0.01) {
      const move = Math.min(span, CRUISE * dt);
      d.x += (dx / span) * move;
      d.y += (dy / span) * move;
      d.z += (dz / span) * move;
    }
    // The leash, applied to where he ended up rather than to where he was
    // aiming: a target just outside it would otherwise tow him out a little
    // further every step.
    const out = Math.hypot(d.x - d.home.x, d.z - d.home.z);
    if (out > LEASH) {
      d.x = d.home.x + ((d.x - d.home.x) / out) * LEASH;
      d.z = d.home.z + ((d.z - d.home.z) / out) * LEASH;
    }

    if (mark !== null) d.yaw = Math.atan2(mark.x - d.x, -(mark.z - d.z));

    /*
     * And the breath itself.
     *
     * Only on a pass, only in range, and on its own clock - so a dragon
     * hovering at the edge of his leash cannot empty a magazine into
     * somebody standing still.
     */
    if (d.mood === "pass" && mark !== null && d.reload <= 0) {
      const gap = Math.hypot(mark.x - d.x, mark.y - d.y, mark.z - d.z);
      if (gap <= BREATH_RANGE) {
        d.reload = BREATH_GAP;
        d.breaths++;
        breaths.push({
          from: dragonMouth(d),
          at: { x: mark.x, y: mark.y + 0.9, z: mark.z },
        });
      }
    }
  }
  return breaths;
}

/** A blow lands. Returns true if that was the one that killed him. */
export function hurtDragon(d: Dragon, damage: number): boolean {
  if (d.slain) return false;
  d.hp -= damage;
  if (d.hp > 0) return false;
  d.hp = 0;
  d.slain = true;
  return true;
}
